import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/auth';
import { toast } from 'sonner';

export type PayrollApprovalRole = 'fishsix_management' | 'accounting';

export interface PayrollApproval {
  id: string;
  month: number;
  year: number;
  management_approved: boolean;
  management_approved_by: string | null;
  management_approved_at: string | null;
  management_note: string | null;
  accounting_approved: boolean;
  accounting_approved_by: string | null;
  accounting_approved_at: string | null;
  accounting_note: string | null;
  created_at: string;
  updated_at: string;
}

// Helper to access payroll schema with type assertion
const payrollSchema = () => (supabase as any).schema('payroll');

export function usePayrollApproval(month: number, year: number) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch approval status for the selected month/year
  const { data: approvalStatus, isLoading: isLoadingApproval } = useQuery({
    queryKey: ['payroll-approval', month, year],
    queryFn: async () => {
      const { data, error } = await payrollSchema()
        .from('teacher_payroll_approvals')
        .select('*')
        .eq('month', month)
        .eq('year', year)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      return data as PayrollApproval | null;
    },
  });

  // Fetch current user's payroll role
  const { data: userRole, isLoading: isLoadingRole } = useQuery({
    queryKey: ['payroll-role', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await payrollSchema()
        .from('payroll_approver_roles')
        .select('role')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching payroll role:', error);
        return null;
      }
      return data?.role as PayrollApprovalRole | null;
    },
    enabled: !!user?.id,
  });

  // Mutation for management approval (step 1)
  const approveManagementMutation = useMutation({
    mutationFn: async (note?: string) => {
      if (!user?.id) throw new Error('User not authenticated');

      // Check if record exists
      const { data: existing } = await payrollSchema()
        .from('teacher_payroll_approvals')
        .select('id')
        .eq('month', month)
        .eq('year', year)
        .single();

      if (existing) {
        // Update existing record
        const { error } = await payrollSchema()
          .from('teacher_payroll_approvals')
          .update({
            management_approved: true,
            management_approved_by: user.id,
            management_approved_at: new Date().toISOString(),
            management_note: note || null,
            updated_at: new Date().toISOString(),
          })
          .eq('month', month)
          .eq('year', year);

        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await payrollSchema()
          .from('teacher_payroll_approvals')
          .insert({
            month,
            year,
            management_approved: true,
            management_approved_by: user.id,
            management_approved_at: new Date().toISOString(),
            management_note: note || null,
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success('อนุมัติสำเร็จ (ขั้นที่ 1)');
      queryClient.invalidateQueries({ queryKey: ['payroll-approval', month, year] });
    },
    onError: (error) => {
      console.error('Management approval error:', error);
      toast.error('เกิดข้อผิดพลาดในการอนุมัติ');
    },
  });

  // Mutation for accounting approval (step 2)
  const approveAccountingMutation = useMutation({
    mutationFn: async (note?: string) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { error } = await payrollSchema()
        .from('teacher_payroll_approvals')
        .update({
          accounting_approved: true,
          accounting_approved_by: user.id,
          accounting_approved_at: new Date().toISOString(),
          accounting_note: note || null,
          updated_at: new Date().toISOString(),
        })
        .eq('month', month)
        .eq('year', year);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('อนุมัติสำเร็จ (ขั้นที่ 2)');
      queryClient.invalidateQueries({ queryKey: ['payroll-approval', month, year] });
    },
    onError: (error) => {
      console.error('Accounting approval error:', error);
      toast.error('เกิดข้อผิดพลาดในการอนุมัติ');
    },
  });

  // Determine if user can approve
  const canApproveStep1 = userRole === 'fishsix_management' && !approvalStatus?.management_approved;
  const canApproveStep2 = userRole === 'accounting' && approvalStatus?.management_approved && !approvalStatus?.accounting_approved;

  return {
    approvalStatus,
    userRole,
    isLoading: isLoadingApproval || isLoadingRole,
    canApproveStep1,
    canApproveStep2,
    approveManagement: (note?: string) => approveManagementMutation.mutate(note),
    approveAccounting: (note?: string) => approveAccountingMutation.mutate(note),
    isApprovingManagement: approveManagementMutation.isPending,
    isApprovingAccounting: approveAccountingMutation.isPending,
  };
}
